import { IdNamePair } from "../../customLists/customListView/CustomListView.types.ts";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";

export function getUniqueFilterOptions<T>(
    items: Array<T> | undefined,
    nameAccessor: (item: T) => string | undefined,
    idAccessor?: (item: T) => string | undefined
): Array<IdNamePair> {
    if (!items) return [];
    const seen = new Set<string>();
    return items
        .filter((item) => {
            const name = nameAccessor(item);
            const id = idAccessor ? idAccessor(item) : name;
            if (!name || !id || seen.has(id)) return false;
            seen.add(id);
            return true;
        })
        .map((item) => ({ id: (idAccessor ? idAccessor(item) : nameAccessor(item))!, name: nameAccessor(item)! }));
}

export function getUniqueNestedFilterOptions<T, N>(
    items: Array<T> | undefined,
    nestedAccessor: (item: T) => Array<N>,
    nameAccessor: (nested: N) => string,
    idAccessor?: (nested: N) => string
): Array<IdNamePair> {
    if (!items) return [];
    const seen = new Set<string>();
    const result: Array<IdNamePair> = [];
    items.forEach((item) => {
        nestedAccessor(item).forEach((nested) => {
            const id = idAccessor ? idAccessor(nested) : nameAccessor(nested);
            const name = nameAccessor(nested);
            if (!seen.has(id)) {
                seen.add(id);
                result.push({ id, name });
            }
        });
    });
    return result;
}

export const sortByName = (a: IdNamePair, b: IdNamePair) => a.name.localeCompare(b.name);

export const sortByEnumValue = (a: IdNamePair, b: IdNamePair) => Number(a.id) - Number(b.id);

export function getScoreFilterFixedData(translateString: (key: string) => string) {
    return [
        { id: "notAvailable", name: translateString("scoreNotEnabled") },
        { id: "noScore", name: translateString("noScore") },
        { id: "1-25", name: "1-25" },
        { id: "26-50", name: "26-50" },
        { id: "51-75", name: "51-75" },
        { id: "76-100", name: "76-100" },
    ];
}

export function matchesScoreFilter(scoreFilter: Array<string>, operator: FilterOperatorUi | undefined, row: { scoreEnabled: boolean; score?: number | null }) {
    if (scoreFilter.length === 0) return true;
    const matches = scoreBucketMatches(scoreFilter, row);
    return operator === FilterOperatorUi.Negative ? !matches : matches;
}

function scoreBucketMatches(scoreFilter: Array<string>, row: { scoreEnabled: boolean; score?: number | null }) {
    if (scoreFilter.includes("notAvailable") && !row.scoreEnabled) return true;
    if (scoreFilter.includes("noScore") && row.scoreEnabled && (row.score === undefined || row.score === null || row.score === 0)) return true;
    if (row.score === undefined || row.score === null || row.score === 0) return false;
    if (scoreFilter.includes("1-25") && row.score >= 1 && row.score <= 25) return true;
    if (scoreFilter.includes("26-50") && row.score >= 26 && row.score <= 50) return true;
    if (scoreFilter.includes("51-75") && row.score >= 51 && row.score <= 75) return true;
    if (scoreFilter.includes("76-100") && row.score >= 76 && row.score <= 100) return true;
    return false;
}
